import * as React from 'react';
import { X, Eye, Hash, Magnet, Layers, Maximize, SlidersHorizontal } from 'lucide-react';
import { ViewSettings } from '@shared/types/tsk';

interface ViewSettingsPanelProps {
  settings: ViewSettings;
  setSettings: (settings: ViewSettings) => void;
  onClose: () => void;
}

const ViewSettingsPanel: React.FC<ViewSettingsPanelProps> = ({ settings, setSettings, onClose }) => {
  const toggle = (key: keyof ViewSettings) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="absolute top-20 left-4 z-[50] w-[calc(100vw-32px)] max-w-[320px] bg-slate-900/95 backdrop-blur-xl border border-white/20 rounded-[2rem] shadow-2xl overflow-hidden animate-in slide-in-from-top-2 duration-200 pointer-events-auto ring-1 ring-black"
      onPointerDown={stopPropagation}
    >
      <div className="px-5 py-4 border-b border-white/5 flex justify-between items-center bg-gradient-to-r from-blue-600/20 to-transparent">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-blue-600 text-white rounded-lg">
            <SlidersHorizontal size={14} />
          </div>
          <div>
            <h2 className="text-[10px] font-black uppercase tracking-[0.15em] text-white">View Config</h2>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 text-white/40 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Compact Toggles */}
        <div className="grid grid-cols-1 gap-2">
          <SettingToggle
            icon={<Layers size={14} />}
            label="Dynamic Fading"
            active={settings.dynamicFading}
            onClick={() => toggle('dynamicFading')}
          />
          <SettingToggle
            icon={<Maximize size={14} />}
            label="Scale with Plan"
            active={settings.scaleWithPlan}
            onClick={() => toggle('scaleWithPlan')}
          />
        </div>

        {/* Symbol Scale Slider */}
        <div className="pt-2 px-1">
          <div className="flex justify-between items-center mb-2">
            <label className="text-[9px] font-black text-white/30 uppercase tracking-widest">Icon Size</label>
            <span className="text-[9px] font-black text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-md">{settings.symbolScale.toFixed(1)}x</span>
          </div>
          <input
            type="range"
            min="0.5"
            max="3.0"
            step="0.1"
            value={settings.symbolScale}
            onChange={(e) => setSettings({ ...settings, symbolScale: parseFloat(e.target.value) })}
            className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
        </div>
      </div>
    </div>
  );
};

interface SettingToggleProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

const SettingToggle: React.FC<SettingToggleProps> = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all border ${active ? 'bg-blue-600/10 border-blue-500/30' : 'bg-white/5 border-transparent hover:bg-white/10'}`}
  >
    <div className="flex items-center gap-3">
      <div className={`${active ? 'text-blue-400' : 'text-slate-500'}`}>
        {icon}
      </div>
      <span className={`text-[10px] font-black uppercase tracking-wider ${active ? 'text-white' : 'text-slate-400'}`}>{label}</span>
    </div>
    <div className={`w-7 h-3.5 rounded-full relative transition-colors ${active ? 'bg-blue-500' : 'bg-slate-700'}`}>
      <div className={`absolute top-0.5 w-2.5 h-2.5 bg-white rounded-full transition-all ${active ? 'right-0.5' : 'left-0.5'}`} />
    </div>
  </button>
);

export default ViewSettingsPanel;